import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout/Layout";
import DashboardPage from "./pages/DashboardPage";
import MarketDataPage from "./pages/MarketDataPage";
import BenchmarkPage from "./pages/BenchmarkPage";
import IntradayEvolutionPage from "./pages/IntradayEvolutionPage";
import PositionHistoryPage from "./pages/PositionHistoryPage";
import LoginPage from "./pages/LoginPage";
import PerformanceChart from "./components/Portfolio/PerformanceChart";
import TradesTable from "./components/Trades/TradesTable";
import PositionsTable from "./components/Positions/PositionsTable";
import DataPage from "./pages/DataPage";
import { getPortfolios, createPortfolio, updatePortfolio, deletePortfolio, clearPortfolioPositions, clearPortfolioTrades, type Portfolio, setStoredSecret, getStoredSecret, clearStoredSecret, api } from "./api/client";
import PortfolioSettingsPage from "./pages/PortfolioSettingsPage";
import NewPortfolioModal from "./components/Portfolio/NewPortfolioModal";
import RecommendationsPage from "./pages/RecommendationsPage";
import ChatPage from "./pages/ChatPage";

export default function App() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!getStoredSecret());
  const [loginError, setLoginError] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Intercept 401 responses globally to trigger re-authentication.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          clearStoredSecret();
          setIsAuthenticated(false);
          setPortfolios([]);
          setSelectedPortfolioId(null);
          setLoginError("Invalid access key. Please try again.");
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, []);

  const handleLogin = async (secret: string) => {
    setStoredSecret(secret);
    try {
      const res = await getPortfolios();
      setIsAuthenticated(true);
      setLoginError("");
      setPortfolios(res.data);
      if (res.data.length > 0) setSelectedPortfolioId(res.data[0].id);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: unknown }; message?: string; code?: string };
      const status = axiosErr?.response?.status;
      console.error("[login] failed:", {
        status,
        data: axiosErr?.response?.data,
        message: axiosErr?.message,
        code: axiosErr?.code,
      });
      clearStoredSecret();
      if (status === 401) {
        setLoginError("Invalid access key. Please try again.");
      } else if (status) {
        setLoginError(`Could not connect to the server (HTTP ${status}). Please try again.`);
      } else {
        setLoginError("Could not connect to the server. Please try again.");
      }
    }
  };

  const handleLogout = () => {
    clearStoredSecret();
    setIsAuthenticated(false);
    setPortfolios([]);
    setSelectedPortfolioId(null);
    setLoginError("");
  };

  const loadPortfolios = () => {
    getPortfolios().then((res) => {
      setPortfolios(res.data);
      if (res.data.length > 0 && !selectedPortfolioId) {
        setSelectedPortfolioId(res.data[0].id);
      }
    });
  };

  useEffect(() => {
    if (isAuthenticated) loadPortfolios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSubmitNewPortfolio = async (values: { name: string; account_id: string; currency: string }) => {
    const res = await createPortfolio(values);
    setPortfolios((prev) => [...prev, res.data]);
    setSelectedPortfolioId(res.data.id);
  };

  const handleRenamePortfolio = async (id: number, newName: string) => {
    try {
      const res = await updatePortfolio(id, { name: newName });
      setPortfolios((prev) => prev.map((p) => (p.id === id ? res.data : p)));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? "Failed to rename portfolio";
      throw new Error(msg);
    }
  };

  const handleDeletePortfolio = async (id: number, _name: string) => {
    try {
      await deletePortfolio(id);
      setPortfolios((prev) => prev.filter((p) => p.id !== id));
      if (selectedPortfolioId === id) {
        const remaining = portfolios.filter((p) => p.id !== id);
        setSelectedPortfolioId(remaining.length > 0 ? remaining[0].id : null);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? "Failed to delete portfolio";
      throw new Error(msg);
    }
  };

  const handleClearPositions = async (id: number) => {
    await clearPortfolioPositions(id);
  };

  const handleClearTrades = async (id: number) => {
    await clearPortfolioTrades(id);
  };

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} error={loginError} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <Layout
              portfolios={portfolios}
              selectedPortfolioId={selectedPortfolioId}
              onSelectPortfolio={setSelectedPortfolioId}
            />
          }
        >
          <Route index element={<DashboardPage portfolioId={selectedPortfolioId} />} />
          <Route path="positions" element={<PositionsTable portfolioId={selectedPortfolioId} />} />
          <Route path="position-history" element={<PositionHistoryPage portfolioId={selectedPortfolioId} />} />
          <Route path="trades" element={<TradesTable portfolioId={selectedPortfolioId} />} />
          <Route path="performance" element={<PerformanceChart portfolioId={selectedPortfolioId} />} />
          <Route path="benchmark" element={<BenchmarkPage portfolioId={selectedPortfolioId} />} />
          <Route path="recommendations" element={<RecommendationsPage portfolioId={selectedPortfolioId} />} />
          <Route path="chat" element={<ChatPage portfolioId={selectedPortfolioId} />} />
          <Route path="import" element={<DataPage />} />
          <Route path="market-data" element={<MarketDataPage />} />
          <Route path="intraday-evolution" element={<IntradayEvolutionPage portfolioId={selectedPortfolioId} />} />
          <Route
            path="portfolio-settings"
            element={
              <PortfolioSettingsPage
                portfolio={portfolios.find((p) => p.id === selectedPortfolioId) ?? null}
                onRename={handleRenamePortfolio}
                onClearPositions={handleClearPositions}
                onClearTrades={handleClearTrades}
                onDelete={handleDeletePortfolio}
                onLogout={handleLogout}
                onCreatePortfolio={() => setShowCreateModal(true)}
              />
            }
          />
        </Route>
      </Routes>
      <NewPortfolioModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleSubmitNewPortfolio}
      />
    </BrowserRouter>
  );
}
